import { CAPABILITY_SECTIONS } from "../templates/capabilities-canonical";
import { CANONICAL_FAQ_PAGE_INTRO, CANONICAL_FAQ_PROPS } from "../templates/faq-canonical";
import { CANONICAL_MAP_PAGE_INTRO, CANONICAL_MAP_PROPS } from "../templates/map-canonical";
import {
    CANONICAL_PROGRAMS_EXPLORER_PROPS,
    CANONICAL_PROGRAMS_PAGE_INTRO,
} from "../templates/programs-canonical";

import type { Page, PageTemplate } from "../schema";

// Canonical defaults for each page template. The hydrate layer (see
// ./hydrate.ts) merges these into a stored room before the schema validator
// runs, so the room JSON can be a thin override layer.
//
// Shape: everything that pageSchema accepts EXCEPT `slug` — slug is the
// stored room's addressing key and always comes from storage. Widget defaults
// are matched into stored widgets by `id` during merge; per-supplier values
// (gdoc URLs, supplier-specific copy) live in stored, everything else lives
// here.
//
// What goes here:
//   - title / navLabel that's identical across all rooms with this template
//   - widget shells with stable IDs and any default props (height, helperText,
//     title, embedded markdown copy)
//   - default `data` block
//   - `hidden` if the page should be off by default
//
// What stays in stored room JSON:
//   - per-supplier doc URLs / envelope IDs / external references
//   - per-supplier data feeds (Your Opportunity snapshot, MAP action plan)
//   - per-room curation directives (customerOverrides, testimonialOverrides,
//     programOverrides, taskOverrides) — the SM uses these to hide/highlight
//     items per supplier without duplicating the canonical content
//   - per-room copy overrides (a page `data.intro` written for the deal;
//     never a first name or a meeting date, the whole supplier team reads it)
//
// Adding an entry opts a template into hydration; templates not in the map
// keep the v0.5 behaviour (stored JSON must hold the full page entry).
export type CanonicalPage = Omit<Partial<Page>, "slug">;

// Marketplace logos come from the shared logo host (source of truth: the
// Notion Marketplace Logo Library), the same base the public website uses.
const LOGOS = "https://logos.bisoncommerce.com";

// Every marketplace Bison sells on, in the public website's order
// (lib/content.ts `marketplaces`, logos from logos.bisoncommerce.com); keep
// the two in step. Rendered on the Welcome page ("Where we sell", 2026-09-09,
// Bill) and in the Capabilities "Who we are" panel. Every logo opens the
// marketplace's site in a new tab.
const MARKETPLACES = [
    { name: "Amazon", logoUrl: `${LOGOS}/marketplace/amazon.png`, href: "https://www.amazon.com" },
    { name: "Walmart", logoUrl: `${LOGOS}/marketplace/walmart.png`, href: "https://www.walmart.com" },
    { name: "Target", logoUrl: `${LOGOS}/marketplace/target.png`, href: "https://www.target.com", logoScale: 0.85 },
    { name: "The Home Depot", logoUrl: `${LOGOS}/marketplace/home-depot.png`, href: "https://www.homedepot.com" },
    { name: "Lowe's", logoUrl: `${LOGOS}/marketplace/lowe-s.png`, href: "https://www.lowes.com" },
    { name: "Wayfair", logoUrl: `${LOGOS}/marketplace/wayfair.png`, href: "https://www.wayfair.com" },
    { name: "Macy's", logoUrl: `${LOGOS}/marketplace/macy-s.png`, href: "https://www.macys.com" },
    { name: "Best Buy", logoUrl: `${LOGOS}/marketplace/best-buy.png`, href: "https://www.bestbuy.com" },
    { name: "Kohl's", logoUrl: `${LOGOS}/marketplace/kohl-s.png`, href: "https://www.kohls.com" },
    { name: "eBay", logoUrl: `${LOGOS}/marketplace/ebay.png`, href: "https://www.ebay.com" },
    { name: "Google Shopping", logoUrl: `${LOGOS}/marketplace/google.png`, href: "https://shopping.google.com" },
    { name: "TikTok Shop", logoUrl: `${LOGOS}/marketplace/tiktok.png`, href: "https://shop.tiktok.com", logoScale: 0.85 },
    { name: "Mathis Home", logoUrl: `${LOGOS}/marketplace/mathis-home.jpg`, href: "https://www.mathishome.com", logoScale: 0.85 },
    { name: "Shopify", logoUrl: `${LOGOS}/marketplace/shopify.png`, href: "https://www.shopify.com", logoScale: 0.85 },
];

export const canonicalPages: Partial<Record<PageTemplate, CanonicalPage>> = {
    // ─── Welcome ────────────────────────────────────────────────────────
    // Page-level shell, the marketplace strip and the customer logo wall. The "What this deal room is
    // for" section and the "Next step" rail card are rendered by
    // templates/Welcome.tsx (canonical copy there, the Checklist state);
    // the stored `rich_text#intro-message`, `content_card#next-step` and the
    // canonical `customer_testimonials#testimonials` are retired from Welcome
    // (2026-09-08, Bill; hydrate.ts drops a stored copy). Nothing on this page
    // names an individual or a date: the supplier's whole team reads it.
    //
    // Per-room curation via customer_logos.customerOverrides: the SM hides
    // specific logos for a room (e.g. when a featured customer is a
    // direct competitor of the supplier) without duplicating the canonical
    // arrays into stored JSON.
    welcome: {
        title: "Welcome to your deal room",
        // "Home" in the left nav since 2026-09-09 (Bill); the template and
        // the page key stay `welcome`.
        navLabel: "Home",
        template: "welcome",
        widgets: [
            {
                // Where we sell (2026-09-09, Bill): every marketplace logo,
                // clickable, above the brand wall. The per-room storefront
                // band (`dtc`) is stored once, on the Capabilities page's
                // marketplace_grid; widgets/MarketplaceGrid.tsx borrows it
                // here, so a room stores nothing for this widget.
                type: "marketplace_grid",
                id: "where-we-sell",
                props: {
                    title: "Where we sell",
                    description:
                        "Every marketplace where Bison sells as the active seller, and our own storefronts for your category. Click a logo to open the site.",
                    marketplaces: MARKETPLACES,
                },
            },
            {
                type: "customer_logos",
                id: "current-customers",
                props: {
                    title: "Brands that trust Bison",
                    description:
                        "Here are a few of Bison's 350+ brand partners that sell in your categories, styles, and/or price points. We are happy to make introductions to any of them on the next call.",
                    customers: [
                        { id: "tvilum", name: "Tvilum", logoUrl: `${LOGOS}/supplier/tvilum.svg`, href: "https://tvilum.com" },
                        { id: "jofran", name: "Jofran", logoUrl: `${LOGOS}/supplier/jofran.png`, href: "https://jofran.com" },
                        { id: "coaster", name: "Coaster", logoUrl: `${LOGOS}/supplier/coaster-co-of-america.png`, href: "https://coastercompany.com" },
                        { id: "liberty-furniture", name: "Liberty Furniture", logoUrl: `${LOGOS}/supplier/liberty-furniture-industries.png`, href: "https://libertyfurniture.com" },
                        { id: "sauder", name: "Sauder", logoUrl: `${LOGOS}/supplier/sauder-woodworking.png`, href: "https://sauder.com" },
                        { id: "flash-furniture", name: "Flash Furniture", logoUrl: `${LOGOS}/supplier/flash-furniture.png`, href: "https://www.flashfurniture.com" },
                        { id: "parker-house", name: "Parker House", logoUrl: `${LOGOS}/supplier/parker-house-furniture.png`, href: "https://parker-house.com" },
                        { id: "meridian", name: "Meridian", logoUrl: `${LOGOS}/supplier/meridian-furniture.png`, href: "https://meridianfurnitureusa.com" },
                    ],
                },
            },
        ],
        data: {
            intro: "Everything we have shared with your team is in this room: the opportunity we see for your catalog, how a Bison partnership works, the agreements, and the checklist that takes us to your first orders. Take a look at your pace; everything here is yours to share.",
        },
    },

    // ─── Capabilities ───────────────────────────────────────────────────
    // Intro, marketplace grid and the What-we-do tour. The sales deck PDF
    // and the FAQ were dropped from this page on 2026-09-04 (Bill): the FAQ
    // lives on its own page (below), the deck is retired.
    // Every widget on this page is shared across rooms. The page-level
    // Fully canonical since 2026-09-07 (Bill): a second nav column beside the
    // room drawer with "Who we are" (intro, About copy, stat band, marketplace
    // strip) and the website sections one at a time
    // (templates/CapabilitiesOverview.tsx). Stored rooms carry
    // { slug, template: "capabilities_overview" } plus the per-room
    // marketplace_grid `dtc` band and nothing else; the old
    // rich_text#capabilities-intro override (it repeated the figures in
    // prose) was dropped from every live room the same day.
    capabilities_overview: {
        title: "What Bison does",
        navLabel: "Capabilities",
        navGroup: "About Us",
        template: "capabilities_overview",
        widgets: [
            {
                // Who we are: the website's About-page mission and team copy,
                // adapted (bisoncommerce.com/about, 2026-09-08). No figures
                // here; the stat band under it carries those.
                type: "rich_text",
                id: "who-we-are",
                props: {
                    markdown: "Bison Commerce is a technology retailer that grew into a full-service marketplace partner for the brands it sells. We learned marketplaces by operating in them every day, at scale, with our own capital on the line, and today we run listings, content, advertising, pricing, fulfillment and customer service for hundreds of brands.\n\nWe win the Buy Box with merchandising and advertising, not a race to the bottom, and we protect brand integrity on every channel. Supplier partnerships are led from our headquarters in Hinsdale, Illinois; engineering, marketplace operations and support run from our operations center in Kaunas, Lithuania. You get a dedicated team, executive access, and the honesty to tell you what will work and what will not.",
                },
            },
            {
                // The website's About-page stat band. Revenue, suppliers,
                // marketplaces and orders are the site's published figures;
                // the team count is Bill's stated figure (2026-09-07). No
                // highlighted figure: Bill read the orange as red (2026-09-07).
                type: "stat_band",
                id: "capabilities-stats",
                props: {
                    items: [
                        { value: "$100M+", label: "Annual revenue" },
                        { value: "350+", label: "Supplier partnerships" },
                        { value: "14+", label: "Marketplace channels" },
                        { value: "400K+", label: "Orders shipped a year" },
                        { value: "85+", label: "Team members" },
                    ],
                },
            },
            {
                // The static list of every marketplace Bison sells on
                // (MARKETPLACES above; every logo links to the marketplace's
                // site in a new tab, Bill 2026-09-07). The per-room `dtc` band
                // (Bison-owned storefronts for this supplier's category) is set
                // by the provisioning skill, never here; the Welcome strip
                // reads it from this widget.
                type: "marketplace_grid",
                id: "marketplaces",
                props: {
                    title: "Where we sell",
                    description: "Every marketplace we operate on as the active seller. Coverage varies by category; we will map yours together on the next call.",
                    marketplaces: MARKETPLACES,
                },
            },
            {
                // The website's About-page leadership list (lib/content.ts
                // `team`), headshots from its processed set copied into
                // public/brand/team/. Keep the two in step.
                type: "team_grid",
                id: "team",
                props: {
                    title: "The team behind it",
                    intro: "Bison is run by a leadership team you can actually reach. Executive access is not a perk, it is how we work with every brand we sell.",
                    members: [
                        { name: "Bill Cronin", role: "CEO", photoUrl: "/brand/team/bill-cronin.webp" },
                        { name: "Inga Linarte", role: "COO", photoUrl: "/brand/team/inga-linarte.webp" },
                        { name: "Audrius Ziemelis", role: "CTO", photoUrl: "/brand/team/audrius-ziemelis.webp" },
                        { name: "Dovile Pundiniene", role: "Head of Finance", photoUrl: "/brand/team/dovile-pundiniene.webp" },
                        { name: "Rasa Statkeviciene", role: "Director of Partnerships", photoUrl: "/brand/team/rasa-statkeviciene.webp" },
                        { name: "Greta Nizeviciene", role: "Head of Supplier Management", photoUrl: "/brand/team/greta-nizeviciene.webp" },
                        { name: "Gabi Bankauskiene", role: "Head of Seller Services", photoUrl: "/brand/team/gabi-bankauskiene.webp" },
                        { name: "Ugne Juskeviciute", role: "Head of Marketplaces", photoUrl: "/brand/team/ugne-juskeviciute.webp" },
                        { name: "Eimante Baksiene", role: "Head of Merchandising", photoUrl: "/brand/team/eimante-baksiene.webp" },
                        { name: "Karolina Svazinskiene", role: "Head of Customer Support", photoUrl: "/brand/team/karolina-svazinskiene.webp" },
                        { name: "Kristina Baltramonaitiene", role: "HR Business Partner", photoUrl: "/brand/team/kristina-baltramonaitiene.webp" },
                    ],
                },
            },
            {
                type: "feature_rows",
                id: "capabilities-tour",
                props: { sections: CAPABILITY_SECTIONS },
            },
        ],
        // No data.intro since 2026-09-08 (Bill): the two About paragraphs are
        // the whole opening, so the panel is not a tagline over a body.
        data: {},
    },

    // ─── FAQ ────────────────────────────────────────────────────────────
    // ─── How We Partner ────────────────────────────────────────────────
    // Plain-language explanation of the 3P drop-ship model for a supplier
    // joining the network (Bill, 2026-09-04). First child of About Us. Fully
    // canonical: stored rooms carry { slug: "how-we-partner", template:
    // "how_we_partner" } and nothing else. Copy has no supplier-name
    // interpolation and no numbers; terms are pointed at the Program Agreement.
    //
    // Rebuilt with visuals 2026-09-08 (Bill, after a prospective-supplier
    // persona review): the same six sections as feature rows, three of them
    // with a scene (the money bar, the MAP guard, the supplier portal) and
    // three text only. The review cut the motion budget from seven loops to
    // three and dropped a growth-grid scene ("exclusivity is the ask, not
    // the illustration"). Rows point at the FAQ, which already answers how
    // orders arrive, who handles damages, allowances and payment. The
    // labelled HomepageJourney hero that opened the page for a few hours
    // the same day was removed by Bill (that scene is never to be used
    // again, anywhere); the page opens on the first row.
    how_we_partner: {
        title: "How we partner",
        navLabel: "How We Partner",
        navGroup: "About Us",
        template: "how_we_partner",
        widgets: [
            {
                type: "feature_rows",
                id: "how-we-partner-rows",
                props: {
                    sections: [
                        {
                            id: "how-we-partner",
                            rows: [
                                {
                                    id: "one-partner",
                                    title: "One partner for all marketplaces",
                                    body: "Bison sells your brand on the marketplaces as the seller of record. One team lists your products, prices them within your MAP, runs the advertising, ships the orders, answers the customer and handles returns. You do not open marketplace accounts or learn six seller portals. You deal with one supplier manager, and that person answers for the results.",
                                    scene: "OneConnection",
                                    sceneScale: 0.95,
                                },
                                {
                                    id: "you-ship-we-sell",
                                    title: "You ship, we sell",
                                    body: "You make the product. When a customer buys on Amazon, Walmart, Target or another marketplace we run for you, our system sends you a purchase order automatically. You pick and pack from your own warehouse and ship on the FedEx or LTL label we provide, on our carrier accounts. From there the order is ours to look after. We track it and keep the customer informed. If a parcel is late or arrives damaged, the customer calls us, not you. Your brand is on the box and Bison is on the receipt.",
                                    scene: "NationwideFulfillment",
                                    sceneScale: 0.88,
                                },
                                {
                                    id: "how-the-money-works",
                                    title: "How the money works",
                                    body: "We buy from you at your wholesale cost, one purchase order at a time, and pay you on the terms we agree before launch. Everything between your wholesale cost and the shelf price is Bison's to manage. That includes the marketplace commission, the advertising, the freight, customer service and the cost of returns. We write damage and defect allowances, marketing co-op and any program terms into the Program Agreement before the first order ships.",
                                    scene: "MoneyWaterfall",
                                },
                                {
                                    id: "what-stays-yours",
                                    title: "What stays yours",
                                    body: "Your brand name is on every listing and your packaging is in every box. The listing copy and photos are yours to approve. You set MAP. Our pricing engine reprices within the floor you set and holds to your MAP requirements. Your own website stays yours too. Many of our partners sell direct and through Bison at the same time, and we coordinate inventory and pricing between the two.",
                                    scene: "ListingCreation",
                                    sceneScale: 0.8,
                                },
                                {
                                    id: "day-to-day",
                                    title: "Manage orders through the supplier portal",
                                    body: "Once we launch, orders land in the supplier portal as purchase orders and you confirm and ship them from there. The same portal shows the status, price and Buy Box position of every SKU. Inventory syncs from your feed, so an item that sells out at your dock comes off the marketplaces at the same time and comes back on the same way.",
                                    scene: "SupplierPortal",
                                },
                                {
                                    id: "where-it-can-go",
                                    title: "Where it can go",
                                    body: "Most partnerships start with part of the catalog on two or three marketplaces. As the numbers come in we widen the assortment and add marketplaces. Where it makes sense for both sides, that grows into an exclusive arrangement where Bison is the only marketplace seller of your brand. The programs cover what we can add along the way: listing optimization, advertising, promotions, new SKU launches and managed seller services.",
                                    scene: "MarketplaceExpansion",
                                },
                            ],
                        },
                    ],
                },
            },
        ],
        data: {
            intro: "How a Bison partnership works: who does what, how an order moves, how you get paid, and what stays yours.",
        },
    },

    // Twenty supplier questions in four sections (2026-09-04, Bill); content
    // lives in ../templates/faq-canonical.ts. Sits under the About Us nav
    // group with Capabilities and Programs. Fully canonical: stored rooms
    // carry { slug: "faq", template: "faq" } and nothing else. The page H1 is
    // the heading, so the widget carries no title of its own.
    faq: {
        title: "Frequently Asked Questions",
        navLabel: "FAQ",
        navGroup: "About Us",
        template: "faq",
        widgets: [
            {
                type: "faq",
                id: "faq-main",
                props: CANONICAL_FAQ_PROPS,
            },
        ],
        data: { intro: CANONICAL_FAQ_PAGE_INTRO },
    },

    // ─── Programs ──────────────────────────────────────────────────────
    // 19-program catalogue (the Notion Programs & Playbooks B2C list, tier
    // for tier) lives in ../templates/programs-canonical.ts, with each
    // program icon and its documents. Per-room overrides via
    // programs_explorer.props.programOverrides — see ./hydrate.ts. The
    // brochure PDFs sit inside each program modal, so there is no
    // separate program-docs grid any more (2026-09-05, Bill).
    programs: {
        title: "Bison programs",
        navLabel: "Programs",
        navGroup: "About Us",
        template: "programs",
        widgets: [
            {
                type: "programs_explorer",
                id: "programs-explorer",
                props: CANONICAL_PROGRAMS_EXPLORER_PROPS,
            },
        ],
        data: { intro: CANONICAL_PROGRAMS_PAGE_INTRO },
    },

    // ─── Action Plan / MAP ─────────────────────────────────────────────
    // 5-phase, 22-task Mutual Action Plan lives in ../templates/map-canonical.ts.
    // All tasks default to `status: pending`. Per-room status mutations live
    // in the sidecar state file under taskStatus[<widgetId>:<taskId>]; per-room
    // task hiding is via props.taskOverrides[].
    map: {
        title: "Checklist",
        navLabel: "Checklist",
        navGroup: "Onboarding",
        template: "map",
        widgets: [
            {
                type: "map",
                id: "map-main",
                props: CANONICAL_MAP_PROPS,
            },
        ],
        data: { intro: CANONICAL_MAP_PAGE_INTRO },
    },

    // ─── Partnership ───────────────────────────────────────────────────
    // Per-supplier copy of the Partnership gdoc. Stored room only contributes
    // the URL of the personalized copy (created by /personalize-deal-room-form).
    //
    // `url` is deliberately empty here and `gdoc_embed` requires a valid URL,
    // so a room that lists this page without supplying a URL FAILS schema
    // validation. That is why the Partnership tab used to vanish from rooms
    // provisioned without Drive rather than degrade. The provisioner's rule
    // The page intro is one canonical sentence (2026-09-03); the stored room
    // never overrides it.
    // (2026-09-03): ship the page either way — with the personalized copy when
    // Drive is available, otherwise with the template URL plus a
    // personalize-pending marker on the widget's helperText. Nothing renders
    // under the doc frame since 2026-09-04; the marker is data only. See
    // /create-deal-room step 6.
    commercial_proposal: {
        title: "Partnership Form",
        navLabel: "Partnership Form",
        navGroup: "Onboarding",
        template: "commercial_proposal",
        widgets: [
            {
                type: "gdoc_embed",
                id: "partnership-form",
                props: {
                    height: 1400,
                    url: "",
                },
            },
        ],
        data: {
            intro: "This is the partnership form, where we write down all the details of our partnership.",
        },
    },

    // ─── Partnership Form (in-room, 2026-10-28) ──────────────────────────
    // The questions are code (forms/partnership-form.ts) and the answers
    // live in their own per-room file, so the page stores nothing but its slug.
    partnership_form: {
        title: "Partnership Form",
        navLabel: "Partnership Form",
        navGroup: "Onboarding",
        template: "partnership_form",
        widgets: [],
    },

    // ─── Contracts ─────────────────────────────────────────────────────
    // The page intro is canonical; the widget list is empty here because the
    // agreement list itself lives in stored JSON as ONE `agreements` widget
    // (per-room: draft URLs, envelope ids, signing state). The renderer is a
    // document list with a reading pane (2026-09-07, Bill): each row shows the
    // draft until a real DocuSign envelope GUID lands on it, then the same row
    // shows the signature face. The "Agreement bundle" rich_text that used to
    // sit above the list is gone (2026-09-07); what each document does is the
    // one-line description on its row.
    contracts: {
        title: "Agreements",
        navLabel: "Agreements",
        navGroup: "Onboarding",
        template: "contracts",
        widgets: [],
        data: {
            intro: "These are the draft agreements for our partnership. Click any document to read or download it.",
        },
    },

    // ─── Marketing ─────────────────────────────────────────────────────
    // Per-supplier copy of the Marketing Partnership gdoc. Same shape as
    // partnership/onboarding — stored contributes only the URL, filled by
    // /personalize-marketing-form.
    marketing: {
        title: "Marketing",
        navLabel: "Marketing",
        template: "marketing",
        widgets: [
            {
                type: "gdoc_embed",
                id: "marketing-form",
                props: {
                    title: "Marketing form",
                    height: 1400,
                    // url must come from stored. Canonical leaves it empty so
                    // a stub room without a URL fails schema validation (the
                    // gdoc_embed widget requires a valid URL) instead of
                    // rendering a broken iframe silently.
                    url: "",
                },
            },
        ],
        data: {},
    },

    // ─── Onboarding ────────────────────────────────────────────────────
    // Per-supplier copy of the Onboarding intake gdoc.
    onboarding: {
        title: "Onboarding",
        navLabel: "Onboarding",
        navGroup: "Onboarding",
        template: "onboarding",
        widgets: [
            {
                type: "gdoc_embed",
                id: "onboarding-form",
                props: {
                    title: "Onboarding intake",
                    height: 1400,
                    url: "",
                },
            },
        ],
        data: {},
    },

    // ─── Uploads ───────────────────────────────────────────────────────
    // Pure canonical — no widgets, just the page intro. Stored room can be
    // { slug: "documents", template: "uploads" }. The page key / template
    // stays `uploads` (the API's upload endpoints share the name); only the
    // slug, title and nav label read "Documents" (2026-09-04, Bill).
    uploads: {
        title: "Documents",
        navLabel: "Documents",
        navGroup: "Onboarding",
        template: "uploads",
        widgets: [],
        data: {
            intro: "Drop files here to share them with the room. Anyone signed in here can view and add files.",
        },
    },

    // ─── Content Optimization room (2026-09-23, Eric) ─────────────────
    // Flat nav, no Home: the proposal's four sections, the assortment
    // matrix, Our capabilities (one page, fully canonical: the website's
    // content-optimization rows scoped to the services on offer, rendered by
    // templates/COCapabilities.tsx), then Checkout. The narrative pages store
    // per-room copy; only the shells are canonical.
    // The proposal report as one page (its own hero is the H1; the stored
    // `data.html` is the report body, `data.sourcesHtml` its footnotes,
    // `data.sections` the in-page anchors the sidebar lists).
    co_proposal: {
        title: "Your Amazon opportunity",
        navLabel: "Amazon opportunity",
        template: "co_proposal",
        widgets: [],
        data: {
            sections: [
                { id: "findings", label: "Findings" },
                { id: "shopper-journey", label: "Shopper journey" },
                { id: "opportunity", label: "Opportunity" },
                { id: "example-product", label: "Example product" },
            ],
        },
    },
    co_assortment: { title: "Your assortment", navLabel: "Your assortment", template: "co_assortment", widgets: [] },
    co_capabilities: {
        title: "Our capabilities",
        navLabel: "Our capabilities",
        template: "co_capabilities",
        widgets: [],
        // The site's content-optimization hero line, verbatim.
        data: { intro: "A quantified Healthscore, keyword and image optimization, A+ content, and review analysis, across your whole catalog." },
    },
    co_checkout: { title: "Checkout", navLabel: "Checkout", template: "co_checkout", widgets: [] },
};
